/**
 * CourseTop Component
 *
 * Displays the top section of the course page, including academy info, complementary info, and user info.
 * Passes relevant props to child components for dynamic rendering and state management.
 *
 * Features:
 * - Renders AcademyInfo with all course and user data
 * - Renders ComplementaryInfo with course statistics and extra info
 * - Renders UserInfo for user-related actions
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.dataCourse - Data object containing all course info and statistics
 * @returns {JSX.Element} The rendered top section of the course page
 */
import PropTypes from 'prop-types';

import AcademyInfo from '../AcademyInfo/AcademyInfo';
import ComplementaryInfo from '../ComplementaryInfo/ComplementaryInfo';
import UserInfo from '../UserInfo/UserInfo';
import classes from './CourseTop.module.css';

const CourseTop = ({ dataCourse }) => {
  return (
    <aside className={classes.courseTopContainer}>
      <div className={classes.courseInfoContainer}>
        <AcademyInfo
          data={dataCourse.data} // Prop passed: course data
          userAditionalTrack={dataCourse.userAditionalTrack} // Prop passed: user activity tracking
          lessons={dataCourse.lessonsCourse} // Prop passed: lessons data
          accessCourse={dataCourse.accessCourse} // Prop passed: access state
          trailerCourse={dataCourse.trailerCourse} // Prop passed: trailer video data
        />
        <ComplementaryInfo
          data={dataCourse.data} // Prop passed: course data
          aditionalData={dataCourse.aditionalData} // Prop passed: additional course info
          usersInCourse={dataCourse.usersInCourse} // Prop passed: enrolled students count
        />
        <UserInfo />
      </div>
    </aside>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
CourseTop.propTypes = {
  /**
   * Data object containing all course info and statistics
   * Passed as prop to render top section
   */
  dataCourse: PropTypes.object
};

CourseTop.defaultProps = {
  dataCourse: {}
};

export default CourseTop;
